import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating, ScoreRing, Loading, EmptyState, VerifiedBadge, ErrorState, disclosureLine } from "@/components/shared";
import { MessageSquare, Flag, PenLine, Scale, MapPin, Globe, ShieldCheck, ThumbsUp, Banknote, Gauge, Clock, Siren, Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const COVER = "https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxvZmZpY2UlMjB0ZWNoJTIwd29ya2Vyc3xlbnwwfHx8fDE3ODIzMTM4ODl8MA&ixlib=rb-4.1.0&q=85";

function RatingBar({ label, value }) {
  const pct = value ? (value / 5) * 100 : 0;
  return (
    <div className="flex items-center gap-4 py-2.5">
      <span className="w-32 shrink-0 text-sm text-slate-400">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-black/[0.04]">
        <div className="h-full rounded-full bg-teal-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-9 shrink-0 text-right font-heading text-sm font-semibold text-ink">{value ? value.toFixed(1) : "-"}</span>
    </div>
  );
}

export default function CompanyProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [interviews, setInterviews] = useState(null);
  const [sort, setSort] = useState("helpful");
  const [voted, setVoted] = useState({});
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [dispute, setDispute] = useState({ open: false, reviewId: null, employer_name: "", reason: "" });
  const [report, setReport] = useState({ open: false, reviewId: null, reason: "Fabricated or fake review", detail: "" });

  const loadReviews = (sortBy) => {
    api.get(`/companies/${id}/reviews`, { params: { sort: sortBy } }).then(({ data }) => setReviews(data)).catch(() => setReviews([]));
  };

  useEffect(() => {
    api.get(`/companies/${id}`).then(({ data }) => setCompany(data)).catch(() => setErr("Company not found"));
    api.get(`/companies/${id}/salaries`).then(({ data }) => setSalaries(data)).catch(() => setSalaries([]));
    api.get(`/companies/${id}/interviews`).then(({ data }) => setInterviews(data)).catch(() => setInterviews({ count: 0 }));
    loadReviews(sort);
    if (user) api.get("/reviews/my-helpful").then(({ data }) => setVoted(Object.fromEntries(data.map((x) => [x, true])))).catch(() => {});
    if (user) api.get("/me/saved-ids").then(({ data }) => setSaved(data.includes(id))).catch(() => {});
    // eslint-disable-next-line
  }, [id, user]);

  const toggleSave = async () => {
    if (!user) { toast.error("Log in to save companies."); nav("/login"); return; }
    try {
      const { data } = await api.post(`/companies/${id}/save`);
      setSaved(data.saved);
      toast.success(data.saved ? "Saved to your dashboard." : "Removed from saved.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const onSort = (v) => { setSort(v); setReviews(null); loadReviews(v); };

  const toggleHelpful = async (rid) => {
    if (!user) { toast.error("Log in to mark reviews helpful."); nav("/login"); return; }
    try {
      const { data } = await api.post(`/reviews/${rid}/helpful`);
      setVoted((prev) => ({ ...prev, [rid]: data.voted }));
      setReviews((prev) => prev.map((r) => (r.id === rid ? { ...r, helpful_count: data.helpful_count } : r)));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const submitDispute = async () => {
    try {
      await api.post("/disputes", {
        review_id: dispute.reviewId, reason: dispute.reason,
        employer_name: dispute.employer_name || "Employer", legal_basis: "defamation",
      });
      toast.success("Dispute filed. Our moderation team will review it.");
      setDispute({ open: false, reviewId: null, employer_name: "", reason: "" });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const submitReport = async () => {
    try {
      await api.post(`/reviews/${report.reviewId}/report`, { reason: report.reason, detail: report.detail });
      toast.success("Report submitted. Our moderation team will review it.");
      setReport({ open: false, reviewId: null, reason: "Fabricated or fake review", detail: "" });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (err) return <ErrorState message={err} />;
  if (!company) return <Loading label="Loading company" />;

  const schema = {
    "@context": "https://schema.org", "@type": "Organization", name: company.name,
    description: company.description, url: company.website,
    aggregateRating: company.review_count ? {
      "@type": "AggregateRating", ratingValue: company.avg_overall, reviewCount: company.review_count, bestRating: 5,
    } : undefined,
  };

  return (
    <main className="mx-auto max-w-[1800px] px-6 py-8 sm:px-10 lg:px-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl border border-[#DDDAD1]">
        <img src={COVER} alt="" aria-hidden="true" className="h-40 w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 to-transparent" />
        <div className="relative -mt-16 flex flex-col gap-4 p-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold sm:text-4xl">{company.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-teal-400" />{company.location || "-"}</span>
              {company.market && company.market !== "Other" && company.market !== company.location && (
                <span className="rounded-full bg-[#E7F0EE] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-teal-600">{company.market} hub</span>
              )}
              {company.website && <a href={company.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-teal-400"><Globe className="h-4 w-4 text-teal-400" />Website</a>}
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-success-500" />{company.licensing_status}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 self-start">
          <button data-testid="write-review-btn" onClick={() => nav(`/company/${id}/review`)} className="btn-teal inline-flex items-center gap-2 self-start">
            <PenLine className="h-4 w-4" /> Write a Review
          </button>
          <button data-testid="compare-btn" onClick={() => nav(`/compare?a=${id}`)} className="inline-flex items-center gap-2 self-start rounded-full border border-[#DDDAD1] px-6 py-3 text-sm text-slate-200 hover:border-teal-400/40">
            <Scale className="h-4 w-4" /> Compare
          </button>
          <button data-testid="save-btn" onClick={toggleSave}
            className={`inline-flex items-center gap-2 self-start rounded-full border px-6 py-3 text-sm transition-colors ${saved ? "border-teal-400 bg-teal-500/10 text-teal-300" : "border-[#DDDAD1] text-slate-200 hover:border-teal-400/40"}`}>
            {saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />} {saved ? "Saved" : "Save"}
          </button>
          </div>
        </div>
      </div>

      {/* Bento scores: brand rating card */}
      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="card-surface flex flex-col items-center justify-center p-8 text-center lg:col-span-4">
          <p className="font-heading text-6xl font-semibold leading-none text-ink">{company.avg_overall ? company.avg_overall.toFixed(1) : "-"}</p>
          <div className="mt-3"><StarRating value={company.avg_overall} size={18} /></div>
          <p className="mt-3 text-sm text-slate-500">Based on {company.review_count} reviews</p>
          {company.red_flag_count > 0 && (
            <p className="mt-3 flex items-center gap-1.5 rounded-full border border-coral-500/30 bg-coral-500/10 px-3 py-1 text-sm text-coral-500"><Flag className="h-4 w-4" /> {company.red_flag_count} red flags</p>
          )}
        </div>
        <div className="card-surface flex flex-col justify-center p-6 lg:col-span-8">
          <p className="eyebrow mb-3">Rating breakdown</p>
          <RatingBar label="Overall" value={company.avg_overall} />
          <RatingBar label="Culture & Values" value={company.avg_culture} />
          <RatingBar label="Compensation" value={company.avg_pay} />
          <RatingBar label="Management" value={company.avg_management} />
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {[["Remote culture", company.remote_culture], ["Employment norm", company.employment_norm], ["Industry", company.industry]].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-[#DDDAD1] bg-ink-900/50 p-4 text-sm">
            <span className="text-slate-500">{k}: </span><span className="text-slate-200">{v}</span>
          </div>
        ))}
      </div>

      {/* Salary insights */}
      <div className="mt-10">
        <div className="flex items-center gap-2">
          <Banknote className="h-5 w-5 text-teal-400" aria-hidden="true" />
          <h2 className="text-2xl font-bold">Salary insights</h2>
        </div>
        {salaries.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-[#DDDAD1] p-6 text-sm text-slate-400" data-testid="salary-empty">
            No salary data shared yet. Add your pay when you write a review to help others.
          </p>
        ) : (
          <div className="mt-5 grid gap-4 md:grid-cols-2" data-testid="salary-panel">
            {salaries.map((s) => {
              const span = Math.max(s.max - s.min, 1);
              const avgPct = ((s.avg - s.min) / span) * 100;
              const fmt = (n) => `${s.currency === "EUR" ? "€" : s.currency === "USD" ? "$" : s.currency + " "}${n.toLocaleString()}`;
              return (
                <div key={s.role} className="card-surface p-5" data-testid="salary-role">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-slate-200">{s.role}</p>
                    <span className="text-xs text-slate-500">{s.count} report{s.count > 1 ? "s" : ""}</span>
                  </div>
                  <p className="mt-2 font-heading text-2xl font-semibold text-ink">{fmt(s.avg)}<span className="ml-1 text-sm font-normal text-slate-500">/{s.period}</span></p>
                  <div className="relative mt-4 h-2 rounded-full bg-black/[0.04]">
                    <div className="absolute inset-y-0 rounded-full bg-teal-500/30" style={{ left: 0, right: 0 }} />
                    <div className="absolute -top-1 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-ink-900 bg-teal-400" style={{ left: `${avgPct}%` }} title="Average" />
                  </div>
                  <div className="mt-2 flex justify-between text-xs text-slate-500">
                    <span>{fmt(s.min)}</span><span>{fmt(s.max)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Interview insights */}
      {interviews && interviews.count > 0 && (
        <div className="mt-10" data-testid="interview-panel">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-teal-400" aria-hidden="true" />
            <h2 className="text-2xl font-bold">Interview insights</h2>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            <div className="card-surface p-5">
              <p className="text-xs uppercase tracking-wide text-slate-500">Difficulty</p>
              <p className="mt-2 font-heading text-3xl font-semibold text-ink">{interviews.avg_difficulty}<span className="text-lg text-slate-500">/5</span></p>
              <div className="mt-3 h-2 rounded-full bg-black/[0.04]"><div className="h-full rounded-full bg-teal-500" style={{ width: `${(interviews.avg_difficulty / 5) * 100}%` }} /></div>
            </div>
            <div className="card-surface p-5">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500"><Clock className="h-3.5 w-3.5" /> Typical length</p>
              <p className="mt-2 font-heading text-2xl font-semibold text-ink">{interviews.common_length}</p>
              <p className="mt-1 text-xs text-slate-500">{interviews.count} experiences shared</p>
            </div>
            <div className="card-surface p-5">
              <p className="text-xs uppercase tracking-wide text-slate-500">Positive experience</p>
              <p className="mt-2 font-heading text-3xl font-semibold text-teal-400">{interviews.positive_pct}%</p>
              <p className="mt-1 text-xs text-slate-500">rated the process positively</p>
            </div>
          </div>
          {interviews.outcomes?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {interviews.outcomes.map((o) => (
                <span key={o.label} className="rounded-full border border-[#DDDAD1] bg-ink-900 px-3 py-1.5 text-xs text-slate-300">
                  {o.label} · <span className="text-teal-400">{o.pct}%</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reviews */}
      <div className="mt-12 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold">Employee reviews</h2>
        {reviews && reviews.length > 0 && (
          <Select value={sort} onValueChange={onSort}>
            <SelectTrigger data-testid="review-sort" className="w-44 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[#DDDAD1] bg-ink-900">
              <SelectItem value="helpful">Most helpful</SelectItem>
              <SelectItem value="recent">Most recent</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      {reviews === null ? (
        <Loading label="Loading reviews" />
      ) : reviews.length === 0 ? (
        <div className="mt-6"><EmptyState icon={MessageSquare} title="No reviews yet. Be the first to share." subtitle="Write a review and help others make confident decisions."
          action={<button onClick={() => nav(`/company/${id}/review`)} className="btn-teal mt-2 text-sm">Write a review</button>} /></div>
      ) : (
        <div className="mt-6 space-y-5">
          {reviews.map((r) => (
            <article key={r.id} data-testid="review-item" className="card-surface p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-lg font-semibold">{r.title}</h3>
                    {r.verified_employee && <VerifiedBadge />}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{disclosureLine(r)} · {r.contract_type}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StarRating value={r.rating_overall} />
                  <span className="font-heading text-lg font-bold text-teal-400">{r.rating_overall?.toFixed(1)}</span>
                </div>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div><p className="text-xs font-semibold uppercase tracking-wide text-success-500">Pros</p><p className="mt-1 text-sm text-slate-300">{r.pros}</p></div>
                <div><p className="text-xs font-semibold uppercase tracking-wide text-coral-500">Cons</p><p className="mt-1 text-sm text-slate-300">{r.cons}</p></div>
              </div>

              {r.red_flags?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {r.red_flags.map((f) => (
                    <span key={f} className="flex items-center gap-1 rounded-full border border-coral-500/30 bg-coral-500/10 px-2.5 py-1 text-xs text-coral-500">
                      <Flag className="h-3 w-3" /> {f}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#DDDAD1] pt-3 text-xs text-slate-500">
                <span>Payout reliability: {r.payout_reliability ? `${r.payout_reliability}/5` : "N/A"} · Compliance: {r.regulator_compliance}</span>
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    data-testid="helpful-btn"
                    onClick={() => toggleHelpful(r.id)}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors ${voted[r.id] ? "border-teal-400 bg-teal-500/10 text-teal-300" : "border-[#DDDAD1] text-slate-400 hover:border-teal-400/40 hover:text-teal-300"}`}
                  >
                    <ThumbsUp className={`h-3.5 w-3.5 ${voted[r.id] ? "fill-teal-400" : ""}`} /> Helpful
                    <span className="font-heading font-semibold">{r.helpful_count || 0}</span>
                  </button>
                  <Dialog open={dispute.open && dispute.reviewId === r.id} onOpenChange={(o) => setDispute((d) => ({ ...d, open: o, reviewId: r.id }))}>
                  <DialogTrigger asChild>
                    <button data-testid="dispute-btn" className="flex items-center gap-1 text-slate-400 hover:text-coral-500">
                      <Scale className="h-3.5 w-3.5" /> Dispute
                    </button>
                  </DialogTrigger>
                  <DialogContent className="border-[#DDDAD1] bg-ink-900">
                    <DialogHeader><DialogTitle>File a dispute / legal request</DialogTitle></DialogHeader>
                    <p className="text-sm text-slate-400">Employers can flag a review for defamation or factual inaccuracy. It enters our moderation queue with a documented audit trail.</p>
                    <Input data-testid="dispute-employer" placeholder="Your company / representative name" value={dispute.employer_name}
                      onChange={(e) => setDispute((d) => ({ ...d, employer_name: e.target.value }))} className="border-[#DDDAD1] bg-ink-900" />
                    <Textarea data-testid="dispute-reason" placeholder="Explain the legal basis or factual dispute…" value={dispute.reason}
                      onChange={(e) => setDispute((d) => ({ ...d, reason: e.target.value }))} className="border-[#DDDAD1] bg-ink-900" />
                    <DialogFooter>
                      <button data-testid="dispute-submit" onClick={submitDispute} disabled={!dispute.reason} className="btn-teal text-sm disabled:opacity-50">Submit dispute</button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                  <Dialog open={report.open && report.reviewId === r.id} onOpenChange={(o) => setReport((d) => ({ ...d, open: o, reviewId: r.id }))}>
                    <DialogTrigger asChild>
                      <button data-testid="report-btn" className="flex items-center gap-1 text-slate-400 hover:text-coral-500">
                        <Siren className="h-3.5 w-3.5" /> Report
                      </button>
                    </DialogTrigger>
                    <DialogContent className="border-[#DDDAD1] bg-ink-900">
                      <DialogHeader><DialogTitle>Report this review</DialogTitle></DialogHeader>
                      <p className="text-sm text-slate-400">Report reviews that break our rules: fabricated content, named individuals, personal data or harassment. Not reviews you simply disagree with.</p>
                      <Select value={report.reason} onValueChange={(v) => setReport((d) => ({ ...d, reason: v }))}>
                        <SelectTrigger data-testid="report-reason" className="border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
                        <SelectContent className="border-[#DDDAD1] bg-ink-900">
                          {["Fabricated or fake review", "Names or identifies an individual", "Contains personal or confidential data", "Discriminatory, harassing or threatening", "Part of a coordinated campaign"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Textarea data-testid="report-detail" placeholder="Add any detail that supports your report (optional)…" value={report.detail}
                        onChange={(e) => setReport((d) => ({ ...d, detail: e.target.value }))} className="border-[#DDDAD1] bg-ink-900" />
                      <DialogFooter>
                        <button data-testid="report-submit" onClick={submitReport} className="btn-teal text-sm">Submit report</button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
